<template>
  <div class="rm rm-theme rm-g">
    <!-- Početna: izbor izveštaja -->
    <template v-if="!currentScreen">
      <div class="rm-home-header">
        <span v-if="restaurantName" class="rm-muted">{{ restaurantName }}</span>
        <h1 class="rm-home-header__title">{{ $t("guest_report") }}</h1>
        <button v-if="isAdmin" type="button" class="rm-view-switch" @click="filterSheet = true">
          {{ restaurantsLabel }}
          <v-icon size="18" color="#9a3412">mdi-chevron-down</v-icon>
        </button>
      </div>

      <div class="rm-chip-row rm-home-periods">
        <button
          v-for="preset in quickPresets"
          :key="preset.key"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': activePresetKey === preset.key }"
          :aria-pressed="String(activePresetKey === preset.key)"
          @click="applyPeriod(preset)"
        >
          {{ presetChipLabel(preset) }}
        </button>
        <button
          v-if="!activeQuickPreset"
          type="button"
          class="rm-chip rm-chip--lg rm-chip--active rm-num"
          aria-pressed="true"
          @click="periodSheet = true"
        >
          {{ periodShort }}
        </button>
        <button
          type="button"
          class="rm-chip rm-chip--lg rm-chip--icon"
          :aria-label="$t('report_mobile.choose_period')"
          @click="periodSheet = true"
        >
          <v-icon size="20" color="#1c1917">mdi-calendar-blank-outline</v-icon>
        </button>
      </div>

      <div class="rm-home-groups">
        <section v-for="group in menuGroups" :key="group.key" class="rm-home-group">
          <h2 v-if="group.title" class="rm-section-title">{{ group.title }}</h2>
          <div class="rm-card">
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rm-menu-row"
              @click="openScreen(item.key)"
            >
              <span class="rm-menu-row__icon">
                <v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon>
              </span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
                <span class="rm-muted">{{ item.subtitle }}</span>
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- Pojedinačni izveštaj -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button
            v-if="!singleScreen"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.back')"
            @click="closeScreen"
          >
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text" :class="{ 'rm-g-pad': singleScreen }">
            <h1 class="rm-detail-header__title">{{ currentScreen.title }}</h1>
            <span class="rm-muted rm-detail-header__sub">{{ headerSubtitle }}</span>
          </div>
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('search')"
            :aria-pressed="String(searchOpen)"
            @click="toggleSearch"
          >
            <v-icon size="22" color="#1c1917">mdi-magnify</v-icon>
          </button>
          <button
            v-if="hasFilters"
            type="button"
            class="rm-icon-btn"
            :class="{ 'rm-g-active': filtersActive }"
            :aria-label="$t('filters')"
            @click="filterSheet = true"
          >
            <v-icon size="22" :color="filtersActive ? '#9a3412' : '#1c1917'">mdi-tune-variant</v-icon>
          </button>
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.share')"
            @click="actionsSheet = true"
          >
            <v-icon size="22" color="#1c1917">mdi-export-variant</v-icon>
          </button>
        </div>

        <div v-if="searchOpen" class="rm-detail-search">
          <label class="rm-search">
            <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
            <span class="rm-visually-hidden">{{ $t("search") }}</span>
            <input ref="searchInput" v-model="search" type="search" :placeholder="$t('search')" />
          </label>
        </div>

        <div class="rm-chip-row">
          <button type="button" class="rm-chip" @click="periodSheet = true">
            <span class="rm-num">{{ periodShort }}</span>
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
          <button
            v-if="restaurantMode"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allRestaurantsSelected }"
            @click="filterSheet = true"
          >
            {{ restaurantsLabel }}
            <v-icon size="16" :color="allRestaurantsSelected ? '#5f5852' : '#9a3412'">mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>

      <div v-if="needsRestaurants" class="rm-empty">{{ $t("please_select_restaurant_to_view_data") }}</div>
      <div v-else-if="currentScreen.legacy" class="rm-legacy">
        <component :is="currentScreen.component" :key="`${currentScreenKey}-${reloadKey}`" v-bind="screenBindings"></component>
      </div>
      <component
        :is="currentScreen.component"
        v-else
        :key="`${currentScreenKey}-${reloadKey}`"
        v-bind="screenBindings"
        @update:status="status = $event"
        @update:delivery="delivery = $event"
        @open-panel="openPanel"
        @toast="showToast"
      ></component>
    </template>

    <report-mobile-period-sheet
      v-model="periodSheet"
      :from="from"
      :to="to"
      :presets="presets"
      @apply="applyPeriod"
    ></report-mobile-period-sheet>

    <guest-mobile-filter-sheet
      v-model="filterSheet"
      :restaurants="availableRestaurants"
      :selected-ids="selectedRestaurantIds"
      :show-restaurants="isAdmin"
      :show-delivery="Boolean(currentScreen && currentScreen.filters)"
      :delivery="delivery"
      @apply="applyFilters"
    ></guest-mobile-filter-sheet>

    <ui-sheet v-model="actionsSheet" :label="$t('report_mobile.more_options')">
      <ul class="rm-list rm-actions">
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('print')">
            <v-icon size="22" color="#1c1917">mdi-printer-outline</v-icon>
            {{ $t("report_mobile.print_pdf") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('excel')">
            <v-icon size="22" color="#15803d">mdi-microsoft-excel</v-icon>
            {{ $t("report_mobile.download_excel") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('reload')">
            <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
            {{ $t("report_mobile.refresh") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('kitchen')">
            <v-icon size="22" color="#1c1917">mdi-chef-hat</v-icon>
            {{ $t("kitchen_display") }}
          </button>
        </li>
      </ul>
    </ui-sheet>

    <ui-sheet v-model="detailSheet" @input="onDetailInput" :ready="Boolean(detail)" :label="detail ? detail.title : ''">
      <template #title>
        {{ detail.title }}
        <small v-if="detail.subtitle" class="rm-g-sub">{{ detail.subtitle }}</small>
      </template>
      <template #default>
        <component :is="detail.component" :key="detail.key" v-bind="detail.props"></component>
      </template>
    </ui-sheet>

    <div v-if="toast" class="rm-g-toast" role="status">{{ toast }}</div>
  </div>
</template>

<script>
import moment from "moment";
import "../../report/mobile/reportMobileShell.scss";
import "./guestMobile.scss";
import ReportMobilePeriodSheet from "../../report/mobile/ReportMobilePeriodSheet.vue";
import GuestMobileFilterSheet from "./GuestMobileFilterSheet.vue";
import hasPlural from "../../report/shared/hasPlural";
import { buildGuestPeriodPresets, findPresetKey, formatRange, formatShortDate } from "../../report/shared/periods";
import {
  DEFAULT_GUEST_SCREEN,
  ROLE_ADMIN,
  buildGuestMenu,
  buildGuestScreens,
  roleOf,
} from "../guestReportScreens";
import {
  GUEST_QUERY,
  MANAGED_GUEST_QUERY,
  parseDelivery,
  parseIds,
  parseRange,
  parseStatus,
  serializeIds,
} from "../guestReportQuery";
import { declaredProps } from "../declaredProps";

const ISO = "YYYY-MM-DD";
const QUICK_KEYS = ["today", "yesterday", "this_week"];

/**
 * Izveštaj gost na telefonu: početna sa izborom izveštaja, pa izveštaj sa zaglavlјem, periodom i
 * filterima. Isti stil i isto ponašanje kao mobilni /izvestaj. Narudžbe su novi ekran (kartice,
 * status u donjoj kartici), ostalo su stari tabovi koji već imaju prikaz za telefon.
 * Stanje (izveštaj, period, status, isporuka, restorani) ide u adresu.
 */
export default {
  name: "GuestReportMobile",
  mixins: [hasPlural],
  components: { ReportMobilePeriodSheet, GuestMobileFilterSheet },
  data() {
    const { query } = this.$route;
    const range = parseRange(query[GUEST_QUERY.from], query[GUEST_QUERY.to]);
    const today = moment().format(ISO);
    return {
      from: range ? range[0] : today,
      to: range ? range[1] : today,
      status: parseStatus(query[GUEST_QUERY.status]),
      delivery: parseDelivery(query[GUEST_QUERY.delivery]),
      search: "",
      searchOpen: false,
      pendingSearch: "",
      selectedRestaurantIds: [],
      periodSheet: false,
      filterSheet: false,
      actionsSheet: false,
      detail: null,
      detailSheet: false,
      reloadKey: 0,
      pushedScreens: 0,
      toast: "",
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    role() {
      return roleOf(this.user?.type);
    },
    isAdmin() {
      return this.role === ROLE_ADMIN;
    },
    presets() {
      return buildGuestPeriodPresets();
    },
    quickPresets() {
      return this.presets.filter((preset) => QUICK_KEYS.includes(preset.key));
    },
    activePresetKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    activeQuickPreset() {
      return QUICK_KEYS.includes(this.activePresetKey);
    },
    periodShort() {
      if (this.activePresetKey) {
        return this.$t(`report_mobile.presets.${this.activePresetKey}`);
      }
      return formatRange(this.from, this.to);
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = this.localeDate(this.from, { weekday: "long" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    restaurantName() {
      return this.toLatin(this.$store.state.restaurant?.name || "");
    },
    dateRange() {
      return [this.from, this.to];
    },
    screens() {
      return buildGuestScreens((key, params) => this.$t(key, params), this.role, { mobile: true });
    },
    menuGroups() {
      return buildGuestMenu((key, params) => this.$t(key, params), this.role).map((group) => ({
        ...group,
        items: group.items.map((key) => ({ key, ...this.screens[key] })),
      }));
    },
    menuKeys() {
      return this.menuGroups.flatMap((group) => group.items.map((item) => item.key));
    },
    /** Jedan izveštaj (nalog "report_guest"): nema početne ni "nazad". */
    singleScreen() {
      return this.menuKeys.length === 1;
    },
    currentScreenKey() {
      const wanted = this.$route.query[GUEST_QUERY.report];
      if (this.menuKeys.includes(wanted)) {
        return wanted;
      }
      return this.singleScreen ? DEFAULT_GUEST_SCREEN : null;
    },
    currentScreen() {
      return this.currentScreenKey ? this.screens[this.currentScreenKey] : null;
    },
    availableRestaurants() {
      if (!this.isAdmin) {
        return [];
      }
      return (this.user?.restaurants || []).filter((item) => item.settings && item.settings.lice_bez_ugovora);
    },
    restaurantMode() {
      return this.isAdmin && (!this.currentScreen || this.currentScreen.restaurants);
    },
    selectedRestaurants() {
      return this.availableRestaurants.filter((item) => this.selectedRestaurantIds.includes(item.id));
    },
    restaurantIds() {
      return this.selectedRestaurants.map((item) => item.id).join(",");
    },
    allRestaurantsSelected() {
      return this.availableRestaurants.length > 0 && this.selectedRestaurants.length === this.availableRestaurants.length;
    },
    needsRestaurants() {
      return Boolean(this.currentScreen && this.currentScreen.restaurants && this.isAdmin && !this.restaurantIds);
    },
    restaurantsLabel() {
      if (this.selectedRestaurants.length === 1) {
        return this.toLatin(this.selectedRestaurants[0].name);
      }
      if (this.allRestaurantsSelected) {
        return this.$t("guest_desktop.all_restaurants_n", { n: this.availableRestaurants.length });
      }
      return this.pluralT("guest_desktop.restaurants_n", this.selectedRestaurants.length);
    },
    hasFilters() {
      return Boolean(this.isAdmin || (this.currentScreen && this.currentScreen.filters));
    },
    filtersActive() {
      return (this.isAdmin && !this.allRestaurantsSelected) || this.delivery !== null;
    },
    allBindings() {
      return {
        searchQuery: this.search,
        dateRange: this.dateRange,
        selectedDate: this.from,
        selectedStatus: { id: 0, name: "all" },
        selectedDeliveryType: { id: 3, name: "all" },
        selectedRestaurants: this.selectedRestaurants,
        restaurantIds: this.restaurantIds,
        isActiveTab: true,
        status: this.status,
        delivery: this.delivery,
      };
    },
    screenBindings() {
      const all = this.allBindings;
      return declaredProps(this.currentScreen.component).reduce((bindings, key) => {
        if (key in all) {
          bindings[key] = all[key];
        }
        return bindings;
      }, {});
    },
    headerSubtitle() {
      const parts = [this.periodLong];
      if (this.restaurantMode && this.selectedRestaurants.length) {
        parts.push(this.restaurantsLabel);
      }
      return parts.join(" · ");
    },
    urlQuery() {
      const today = moment().format(ISO);
      const query = {};
      if (this.currentScreenKey) {
        query[GUEST_QUERY.report] = this.currentScreenKey;
      }
      if (!(this.from === today && this.to === today)) {
        query[GUEST_QUERY.from] = this.from;
        query[GUEST_QUERY.to] = this.to;
      }
      if (this.currentScreenKey === "orders") {
        if (this.status) {
          query[GUEST_QUERY.status] = this.status;
        }
        if (this.delivery !== null) {
          query[GUEST_QUERY.delivery] = String(this.delivery);
        }
      }
      if (this.isAdmin) {
        const ids = serializeIds(this.selectedRestaurantIds, this.availableRestaurants.length);
        if (ids) {
          query[GUEST_QUERY.restaurants] = ids;
        }
      }
      return query;
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentScreenKey(newKey, oldKey) {
      if (newKey === oldKey) {
        return;
      }
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      this.searchOpen = Boolean(this.search);
      this.detailSheet = false;
      window.scrollTo(0, 0);
    },
  },
  created() {
    const ids = parseIds(this.$route.query[GUEST_QUERY.restaurants]);
    const fromUrl = this.availableRestaurants.filter((item) => ids.includes(item.id)).map((item) => item.id);
    this.selectedRestaurantIds = fromUrl.length ? fromUrl : this.availableRestaurants.map((item) => item.id);
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
  },
  methods: {
    presetChipLabel(preset) {
      const label = this.$t(`report_mobile.presets.${preset.key}`);
      if (this.activePresetKey === preset.key && preset.from === preset.to) {
        return `${label} · ${formatShortDate(preset.from)}`;
      }
      return label;
    },
    applyPeriod({ from, to }) {
      this.from = from;
      this.to = to;
    },
    applyFilters({ restaurantIds, delivery }) {
      if (this.isAdmin) {
        this.selectedRestaurantIds = this.availableRestaurants.filter((item) => restaurantIds.includes(item.id)).map((item) => item.id);
      }
      if (this.currentScreen && this.currentScreen.filters) {
        this.delivery = delivery;
      }
    },
    openScreen(key, { search = "" } = {}) {
      if (key === this.currentScreenKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.pushedScreens += 1;
      this.$router.push({ query: { ...this.$route.query, [GUEST_QUERY.report]: key } }).catch(() => {});
    },
    closeScreen() {
      // Ekran otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad");
      // direktan link (?r=...) → zameni adresu, da ne izađemo sa /guest-report.
      if (this.pushedScreens > 0) {
        this.pushedScreens -= 1;
        this.$router.back();
        return;
      }
      const query = { ...this.$route.query };
      delete query[GUEST_QUERY.report];
      this.$router.replace({ query }).catch(() => {});
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      } else {
        this.search = "";
      }
    },
    runAction(action) {
      this.actionsSheet = false;
      if (action === "print") {
        this.$store.commit("SET_PRINTING");
      } else if (action === "excel") {
        this.$store.commit("SET_EXCEL");
      } else if (action === "reload") {
        this.detailSheet = false;
        this.reloadKey += 1;
      } else if (action === "kitchen") {
        this.$router.push({ name: "kitchen-display" });
      }
    },
    /** Detalji narudžbe (ekran ih javlja kao zapis { key, title, subtitle, component, props }). */
    openPanel(entry) {
      this.detail = entry;
      this.detailSheet = true;
    },
    onDetailInput(open) {
      if (!open) {
        this.$nextTick(() => {
          this.detail = null;
        });
      }
    },
    showToast(text) {
      this.toast = text;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = "";
      }, 3000);
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_GUEST_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },
  },
};
</script>
